/**
 * Shared label + Spark icon slug for the "add data" menu entries, so the
 * wizard "Need a Dataset?" dropdown and the Data Overview / task-page
 * AddDataButton menu stay consistent (Figma 3081-5906).
 */
export const ADD_DATA_MENU_ITEMS = Object.freeze({
    upload: Object.freeze({ label: 'Upload Data', iconSlug: 'upload' }),
    library: Object.freeze({ label: 'Choose from your uploads', iconSlug: 'library' }),
    huggingface: Object.freeze({ label: 'Use Hugging Face', iconSlug: 'huggingface' }),
    generateAi: Object.freeze({ label: 'Generate data with AI', iconSlug: 'sparkles-2' }),
    retrievalAgent: Object.freeze({ label: 'Retrieval Agent', iconSlug: 'agent' }),
    /** SaaS-only request row (Figma 9293-3459): no icon, link-blue text, disabled once filed. */
    deployOnCloud: Object.freeze({
        label: 'I want to deploy platform on my cloud',
        className: 'add-data-menu-item--link',
    }),
});

export const RETRIEVAL_AGENT_MENU_INFO =
    'Find and retrieve public datasets from Hugging Face and Kaggle.';

/** Data Overview / task-page "+" menu order — Upload first. */
export const ADD_DATA_MENU_ORDER = Object.freeze([
    'upload',
    'library',
    'huggingface',
    'generateAi',
    'retrievalAgent',
    'deployOnCloud',
]);

/** Wizard "Need a Dataset?" order (Figma 9293-3459); the cloud-deploy request is always last. */
export const NEED_DATASET_MENU_ORDER = Object.freeze([
    'generateAi',
    'retrievalAgent',
    'huggingface',
    'library',
    'deployOnCloud',
]);

/**
 * Add-data actions in the given `order` (defaults to the Data Overview order).
 * An entry is emitted only when it is both included and has a click handler.
 */
export function buildAddDataMenuItems({
    includeUpload = false,
    includeLibrary = false,
    includeHuggingFace = false,
    includeGenerateAi = false,
    includeRetrievalAgent = false,
    includeDeployOnCloud = false,
    /** Grey out the cloud-deploy row once the user already asked (Figma 9347-2466). */
    deployOnCloudDisabled = false,
    onUpload,
    onLibrary,
    onHuggingFace,
    onGenerateAi,
    onRetrievalAgent,
    onDeployOnCloud,
    order = ADD_DATA_MENU_ORDER,
} = {}) {
    const entries = {
        upload: { include: includeUpload, onClick: onUpload },
        library: { include: includeLibrary, onClick: onLibrary },
        huggingface: { include: includeHuggingFace, onClick: onHuggingFace },
        generateAi: { include: includeGenerateAi, onClick: onGenerateAi },
        retrievalAgent: {
            include: includeRetrievalAgent,
            onClick: onRetrievalAgent,
            extra: { showInfoIcon: true, infoText: RETRIEVAL_AGENT_MENU_INFO },
        },
        deployOnCloud: {
            include: includeDeployOnCloud,
            onClick: onDeployOnCloud,
            extra: { disabled: deployOnCloudDisabled },
        },
    };
    return order.flatMap((key) => {
        const entry = entries[key];
        if (!entry?.include || !entry.onClick) return [];
        return [{ ...ADD_DATA_MENU_ITEMS[key], onClick: entry.onClick, ...entry.extra }];
    });
}

/** Map builder rows onto Spark `PopoverMenu` item props. */
export function toPopoverMenuItems(items, { onClose } = {}) {
    return (items || []).map((item) => ({
        label: item.label,
        showPrefixIcon: Boolean(item.iconSlug),
        prefixIconSlug: item.iconSlug,
        showInfoIcon: Boolean(item.showInfoIcon),
        infoText: item.infoText,
        className: item.className,
        state: item.disabled ? 'disabled' : undefined,
        disableWhenReadOnly: true,
        onClick: () => {
            onClose?.();
            item.onClick?.();
        },
    }));
}
